import { useAgendaAlerts } from "../../../store/global/useAgendaAlerts";
import { AgendaAlertModel } from "../../models/global";

const TOKEN_STORAGE_KEY = "@petland";
const LOGIN_DISPLAY_MODE = "login";
const LOGIN_ALERT_STORAGE_PREFIX = "@petland:agenda-alerts:login";

const hashValue = (value = "") => {
  const text = `${value}`;
  let hash = 0;

  for (let index = 0; index < text.length; index += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash).toString(36);
};

const getSessionFingerprint = () => {
  try {
    return hashValue(localStorage.getItem(TOKEN_STORAGE_KEY) || "anonymous");
  } catch {
    return "anonymous";
  }
};

const getLoginAlertStorageKey = (alert, sessionFingerprint) =>
  `${LOGIN_ALERT_STORAGE_PREFIX}:${sessionFingerprint}:${alert.id}:${hashValue(alert.key)}`;

const isLoginAlert = (alert) => alert?.displayMode === LOGIN_DISPLAY_MODE;

const wasLoginAlertSeen = (alert, sessionFingerprint) => {
  if (!isLoginAlert(alert)) return false;

  try {
    return sessionStorage.getItem(getLoginAlertStorageKey(alert, sessionFingerprint)) === "true";
  } catch {
    return false;
  }
};

const markLoginAlertSeen = (alert, sessionFingerprint) => {
  if (!isLoginAlert(alert)) return;

  try {
    sessionStorage.setItem(getLoginAlertStorageKey(alert, sessionFingerprint), "true");
  } catch (error) {
    console.error(error);
  }
};

export const clearAgendaLoginAlertSession = () => {
  try {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(LOGIN_ALERT_STORAGE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error(error);
  }
};

export default class AgendaAlertController {
  constructor() {
    this.MAgendaAlert = AgendaAlertModel();
    this.actions = useAgendaAlerts.getState().actions;
  }

  init = async () => {
    const currentSessionFingerprint = getSessionFingerprint();
    const { initialized, loading, sessionFingerprint } = useAgendaAlerts.getState().data;

    if (sessionFingerprint && sessionFingerprint !== currentSessionFingerprint) {
      this.actions.resetForSession(currentSessionFingerprint);
    }

    if (initialized && sessionFingerprint === currentSessionFingerprint) return;
    if (loading) return;

    await this.loadAlerts(currentSessionFingerprint);
  };

  loadAlerts = async (sessionFingerprint = getSessionFingerprint()) => {
    this.actions.setLoading(true);
    const alerts = await this.MAgendaAlert.findAlerts();
    this.actions.setLoading(false);

    if (alerts === false) {
      this.actions.setError(true, sessionFingerprint);
      return;
    }

    const visibleAlerts = alerts.filter((alert) => !wasLoginAlertSeen(alert, sessionFingerprint));

    this.actions.setAlerts(visibleAlerts, sessionFingerprint);
  };

  dismissCurrentAlert = async () => {
    const { alerts, currentIndex, dismissing, sessionFingerprint } = useAgendaAlerts.getState().data;
    const currentAlert = alerts[currentIndex];

    if (!currentAlert || dismissing) return;

    markLoginAlertSeen(currentAlert, sessionFingerprint || getSessionFingerprint());

    this.actions.setDismissing(true);
    await this.MAgendaAlert.dismissAlert(currentAlert);
    this.actions.setDismissing(false);
    this.actions.advanceAlert();
  };
}
